import {
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Output,
  ViewChild,
  Component,
  TemplateRef,
  ChangeDetectorRef,
} from "@angular/core";
import { BsModalRef, BsModalService, daLocale } from "ngx-bootstrap";
import { FormGroup, FormBuilder, Validators, AbstractControl, FormControl, ValidatorFn, ValidationErrors, FormArray } from "@angular/forms";
import { Observable, of, Subject } from "rxjs";
import { switchMap, takeUntil, tap } from "rxjs/operators";
import { AnagrafeService } from "@app/shared/services/anagrafe.service";
import { ComuneVO } from "@vo/gescor/comuneVO";
import { AnagraficaBeneficiarioVO } from "@app/shared/vo/anagrafica-beneficiarioVO";
import { DocumentoSpesaVO } from "@app/shared/vo/documento-spesaVO";
import { LineeFinanziamentoEnum, LineeFinanziamentoLabelMapping } from "@app/shared/enum/linee-finanziamento-enum";
import { TipoNotaLiquidazioneEnum } from "@app/shared/enum/tipo-nota-liquidazione-enum";
import { TipoBeneficiarioEnum, TipoBeneficiarioLabelMapping } from "@app/shared/enum/tipo-beneficiario-enum";

@Component({
  selector: "app-modale-aggiungi-beneficiario",
  templateUrl: "./modale-aggiungi-beneficiario.component.html"
})
export class ModaleAggiungiBeneficiarioComponent implements OnInit, OnDestroy {
  @ViewChild("aggiungiBeneficiarioModal", { static: false })
  public aggiungiBeneficiarioModal: TemplateRef<any>;

  @Output()
  emettiDatiBeneficiario: EventEmitter<
    FormGroup
  > = new EventEmitter<FormGroup>();

  anagrafica: AnagraficaBeneficiarioVO = new AnagraficaBeneficiarioVO();

  // Array di beneficiari già inseriti per validazione linea finanziamento
  private beneficiariEsistenti: FormGroup[] = [];

  private destroy$ = new Subject<void>();
  private revalidationTimeout: any = null;

  lineeFinanziamento = Object.values(LineeFinanziamentoEnum) as LineeFinanziamentoEnum[];
  lineeFinanziamentoLabelMapping = LineeFinanziamentoLabelMapping;
  tipiBeneficiario = Object.values(TipoBeneficiarioEnum) as TipoBeneficiarioEnum[];
  tipoBeneficiarioLabelMapping = TipoBeneficiarioLabelMapping;
  percentualiIva: number[] = [0, 4, 5, 10, 22];//Array.from({ length: 40 }, (_, i) => i + 1);
  percentualiRitenuta: any[] = [
    { value: 0, label: 'Non soggetto' },
    { value: 4, label: '4%' }
  ];


  private config = {
    class: "modal-dialog modal-info modal-xlg",
    animated: true,
  };

  public modalRef: BsModalRef;
  public tipoNota: TipoNotaLiquidazioneEnum;

  public formDecreto: FormGroup;


  public modaleAperta: boolean;
  public comuneSelezionato: boolean = false;
  private ultimiComuniRicerca: ComuneVO[] = [];
  private ricercaComuneInCorso: boolean = false;
  private selezioneComunePendingOnBlur: boolean = false;

  public formBeneficiario: FormGroup;
  parentForm: any;

  comuniDataSource: Observable<ComuneVO[]>;

  ngOnInit(): void { }

  ngOnDestroy(): void {
    // Cancella eventuali timeout in sospeso
    if (this.revalidationTimeout) {
      clearTimeout(this.revalidationTimeout);
      this.revalidationTimeout = null;
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  constructor(
    private modalService: BsModalService,
    private cd: ChangeDetectorRef,
    private fb: FormBuilder,
    private anagrafeService: AnagrafeService,
  ) {
  }

  public async openModal(tipoNota: TipoNotaLiquidazioneEnum, formBeneficiarioEdit: FormGroup, beneficiariEsistenti: FormGroup[] = []) {
    this.tipoNota = tipoNota; // Salva il tipo di nota per i validatori
    this.beneficiariEsistenti = beneficiariEsistenti || [];
    this.modaleAperta = false;
    this.comuneSelezionato = false;
    this.modalRef = this.modalService.show(
      this.aggiungiBeneficiarioModal,
      this.config
    );
    this.formBeneficiario = this.fb.group({
      documentiSpesa: this.fb.array([this.createDocumentoSpesaFormGroup()]),
      tipoBeneficiario: [null, Validators.required],
      denominazione: ["", [Validators.required]],
      codiceFiscale: ["", [Validators.required]],
      partitaIva: [""],
      indirizzo: ["", Validators.required],
      comune: ["", [Validators.required, this.comuneValidator()]],
      provincia: [{value: '', disabled: true}],
      cap: ["", [Validators.required, this.capItalianoValidator()]],

      classeContabile: ["", [Validators.required, this.contoValidator(), this.contoContributiValidator()]],
      // codiceFornitore: ["", Validators.required],
      iban: ["", this.getIbanValidators()],
      ritenuta: ["", this.getRitenutaValidators()],
      protocolliDomanda: [""],
      codiceCor: [""],
      noteAggiuntive: [""],
    });

    // Typeahead comuni: si aggiorna al cambio del valore del campo comune
    this.comuniDataSource = this.formBeneficiario.get('comune').valueChanges.pipe(
      switchMap((filtro: string) => {
        // Non fare ricerche se il comune è già stato selezionato
        if (this.comuneSelezionato) {
          return of([]);
        }
        if (!filtro || typeof filtro !== 'string' || filtro.length < 2) {
          this.ultimiComuniRicerca = [];
          return of([]);
        }
        this.ricercaComuneInCorso = true;
        return this.anagrafeService.ricercaComune(filtro).pipe(
          tap(risultati => {
            this.ultimiComuniRicerca = risultati || [];
            this.ricercaComuneInCorso = false;
            if (this.selezioneComunePendingOnBlur) {
              this.trySelectComuneByExactMatch(this.formBeneficiario.get('comune').value, this.ultimiComuniRicerca);
              this.selezioneComunePendingOnBlur = false;
            }
          })
        );
      })
    );

    // Applica le logiche di validazione al primo documento
    this.applicaLogicheValidazioneDocumento(0);

    // Sottoscrizione al cambio del tipo beneficiario
    this.formBeneficiario.get('tipoBeneficiario').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateValidators();
      });

    // Formatta Codice Fiscale in uppercase e rimuovi spazi
    this.formBeneficiario.get('codiceFiscale').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value && typeof value === 'string') {
          const formattedValue = value.toUpperCase().replace(/\s/g, '');
          if (value !== formattedValue) {
            this.formBeneficiario.get('codiceFiscale').setValue(formattedValue, { emitEvent: false });
          }
        }
      });

    // Formatta Partita IVA in uppercase e rimuovi spazi
    this.formBeneficiario.get('partitaIva').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value && typeof value === 'string') {
          const formattedValue = value.toUpperCase().replace(/\s/g, '');
          if (value !== formattedValue) {
            this.formBeneficiario.get('partitaIva').setValue(formattedValue, { emitEvent: false });
          }
        }
      });

    // Formatta IBAN in uppercase
    this.formBeneficiario.get('iban').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value && typeof value === 'string') {
          const formattedValue = value.toUpperCase().replace(/\s/g, '');
          if (value !== formattedValue) {
            this.formBeneficiario.get('iban').setValue(formattedValue, { emitEvent: false });
          }
        }
      });

    this.formBeneficiario.get('comune').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value && typeof value === 'string') {
          const upperValue = value.toUpperCase();
          if (value !== upperValue) {
            this.formBeneficiario.get('comune').setValue(upperValue, { emitEvent: false });
          }
        }
      });

    // Formatta Denominazione in uppercase
    this.formBeneficiario.get('denominazione').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value && typeof value === 'string') {
          const upperValue = value.toUpperCase();
          if (value !== upperValue) {
            this.formBeneficiario.get('denominazione').setValue(upperValue, { emitEvent: false });
          }
        }
      });

    // Formatta Indirizzo in uppercase
    this.formBeneficiario.get('indirizzo').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value && typeof value === 'string') {
          const upperValue = value.toUpperCase();
          if (value !== upperValue) {
            this.formBeneficiario.get('indirizzo').setValue(upperValue, { emitEvent: false });
          }
        }
      });

    // Formatta Provincia in uppercase
    this.formBeneficiario.get('provincia').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value && typeof value === 'string') {
          const upperValue = value.toUpperCase();
          if (value !== upperValue) {
            this.formBeneficiario.get('provincia').setValue(upperValue, { emitEvent: false });
          }
        }
      });






    // Per PAGAMENTO_IMPOSTE, imposta automaticamente PERSONA_GIURIDICA
    if (this.tipoNota === TipoNotaLiquidazioneEnum.PAGAMENTO_IMPOSTE) {
      this.formBeneficiario.get('tipoBeneficiario').setValue(TipoBeneficiarioEnum.PERSONA_GIURIDICA);
      this.updateValidators();
    }

    if (formBeneficiarioEdit) {
      this.popolaFormPerModifica(formBeneficiarioEdit);
    }

    this.modaleAperta = true;
    this.cd.detectChanges();

    // Aggiorna la validità del form dopo che tutto è stato configurato
    this.formBeneficiario.updateValueAndValidity({ emitEvent: true });

    console.log("Form VALIDA: ", this.formBeneficiario.valid);
    if (!this.formBeneficiario.valid) {
      console.log("Form errors in openModal:", this.getFormValidationErrors());
    }


  }







  public chiudiModale() {
    // Marca la modale come chiusa
    this.modaleAperta = false;

    // Cancella eventuali timeout in sospeso prima di chiudere la modale
    if (this.revalidationTimeout) {
      clearTimeout(this.revalidationTimeout);
      this.revalidationTimeout = null;
    }

    this.modalRef.hide();
  }

  salva() {
    this.markFormGroupTouched(this.formBeneficiario);

    if (!this.formBeneficiario.valid) {
      // Trova il primo controllo invalido dal FormGroup (non dal DOM)
      const primoControlloInvalido = this.trovaPrimoControlloInvalido();
      if (primoControlloInvalido) {
        // Usa setTimeout per attendere il ciclo di CD del modal
        setTimeout(() => {
          const modals = document.querySelectorAll('modal-container');
          const lastModal = modals.length > 0 ? modals[modals.length - 1] as HTMLElement : null;
          const scope = lastModal || document.body;
          // Cerca l'elemento DOM per il controllo invalido (per attributo formcontrolname o id)
          var elemento = scope.querySelector('.ng-invalid[formcontrolname]') as HTMLElement;
          if (!elemento) {
            elemento = scope.querySelector('#' + primoControlloInvalido +'.ng-invalid') as HTMLElement;
          }
          if (!elemento) {
            elemento = scope.querySelector('.ng-invalid');
          }
          // Per mat-select, cerca il mat-form-field contenitore
          if (elemento && elemento.tagName === 'MAT-SELECT') {
            var matField = elemento.closest('mat-form-field') as HTMLElement;
            if (matField) {
              elemento = matField;
            }
          }
          if (elemento) {
            // Usa direttamente la modal-container come container di scroll
            this.scrollToInModal(elemento, lastModal);
            elemento.focus();
            this.evidenziaErrore(elemento);
          }
        }, 50);
      }
      return;
    }

    this.emettiDatiBeneficiario.emit(this.formBeneficiario);
    this.chiudiModale();
  }

  /**
   * Trova il nome del primo controllo invalido nel FormGroup, seguendo l'ordine dei campi.
   */
  private trovaPrimoControlloInvalido(): string {
    // Ordine dei campi come appaiono nel template
    var campiOrdine = [
      'denominazione', 'indirizzo', 'comune', 'cap',
      'tipoBeneficiario', 'codiceFiscale', 'partitaIva',
      'classeContabile', 'iban', 'ritenuta'
    ];
    for (var i = 0; i < campiOrdine.length; i++) {
      var control = this.formBeneficiario.get(campiOrdine[i]);
      if (control && control.invalid) {
        return campiOrdine[i];
      }
    }
    // Controlla i documenti di spesa
    var docs = this.formBeneficiario.get('documentiSpesa') as FormArray;
    if (docs) {
      for (var d = 0; d < docs.length; d++) {
        var doc = docs.at(d) as FormGroup;
        var campiDoc = ['lineaFinanziamento', 'importo', 'importoNetto', 'iva', 'cig', 'cup', 'pad'];
        for (var j = 0; j < campiDoc.length; j++) {
          var controlDoc = doc.get(campiDoc[j]);
          if (controlDoc && controlDoc.invalid) {
            return campiDoc[j];
          }
        }
      }
    }
    return null;
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
      if (control instanceof FormArray) {
        control.controls.forEach(c => {
          if (c instanceof FormGroup) {
            this.markFormGroupTouched(c);
          } else {
            c.markAsTouched();
          }
        });
      }
    });
  }

  private scrollToInModal(element: HTMLElement, container: HTMLElement) {
    if (!container) {
      return;
    }
    var containerRect = container.getBoundingClientRect();
    var elementRect = element.getBoundingClientRect();
    container.scrollTop = container.scrollTop + (elementRect.top - containerRect.top) - 100;
  }

  private getScrollableParent(element: HTMLElement): HTMLElement {
    var parent = element.parentElement;
    while (parent) {
      var style = window.getComputedStyle(parent);
      if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return document.body;
  }

  private evidenziaErrore(elemento: HTMLElement) {
    const formGroup = elemento.closest('.form-group') as HTMLElement;
    const target = (elemento.closest('mat-form-field') as HTMLElement)
      || (elemento.querySelector('mat-form-field') as HTMLElement)
      || (elemento.querySelector('input, textarea, select') as HTMLElement)
      || (formGroup && formGroup.querySelector('input:not([readonly]), textarea') as HTMLElement)
      || formGroup
      || elemento;
    target.classList.remove('field-error-highlight');
    void target.offsetWidth;
    target.classList.add('field-error-highlight');
    setTimeout(() => target.classList.remove('field-error-highlight'), 1900);
  }

  isRequiredField(field: string) {
    const form_field = this.formBeneficiario.get(field);
    if (!form_field || !form_field.validator) {
      return false;
    }

    const validator = form_field.validator({} as AbstractControl);
    return (validator && validator.required);
  }

  // Metodo helper per verificare se un campo di un documento specifico è obbligatorio
  isDocumentoFieldRequired(documentoIndex: number, fieldName: string): boolean {
    const documentoGroup = this.documentiSpesaArray.at(documentoIndex) as FormGroup;
    const field = documentoGroup.get(fieldName);

    if (!field || !field.validator) {
      return false;
    }

    const validator = field.validator({} as AbstractControl);
    return (validator && validator.required);
  }

  annulla() {
    this.chiudiModale();
  }



  // Validatori personalizzati
  codiceFiscalePartitaIvaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Se il campo è vuoto, lascia che required si occupi della validazione
      }

      const value = control.value.toString().toUpperCase().replace(/\s/g, '');

      // Pattern Codice Fiscale (16 caratteri alfanumerici)
      const cfPattern = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;

      // Pattern Partita IVA (11 cifre numeriche)
      const piPattern = /^[0-9]{11}$/;

      if (cfPattern.test(value) || piPattern.test(value)) {
        return null;
      }

      return {
        cfPiInvalid: {
          message: 'Inserire un Codice Fiscale valido (16 caratteri) o una Partita IVA valida (11 cifre)'
        }
      };
    };
  }

  comuneValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const value = control.value.toString();

      // Solo lettere (anche accentate), spazi e apostrofi, minimo 2 caratteri
      const comunePattern = /^[a-zA-ZàáâäãåèéêëìíîïòóôöõùúûüñçÀÁÂÄÃÅÈÉÊËÌÍÎÏÒÓÔÖÕÙÚÛÜÑÇ'\s]{2,}$/;

      if (!comunePattern.test(value)) {
        return {
          comuneInvalid: {
            message: 'Il comune deve contenere solo lettere e avere almeno 2 caratteri'
          }
        };
      }

      if (!this.comuneSelezionato) {
        return {
          comuneNonSelezionato: {
            message: 'Selezionare un comune valido dall\'elenco'
          }
        };
      }

      return null;
    };
  }

  provinciaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const value = control.value.toString();

      // Solo lettere maiuscole, esattamente 2 caratteri per i codici provincia italiani
      const provinciaPattern = /^[A-Z]{2}$/;

      if (!provinciaPattern.test(value)) {
        return {
          provinciaInvalid: {
            message: 'La provincia deve essere un codice di 2 lettere maiuscole (es: FI, MI, RM)'
          }
        };
      }

      return null;
    };
  }

  onProvinciaInput(event: any): void {
    const input = event.target;
    const value = input.value.toUpperCase();
    input.value = value;
    this.formBeneficiario.get('provincia').setValue(value);
  }

  /**
   * Callback typeahead selezione comune: compila automaticamente la provincia.
   */
  onComuneSelezionato(evento: any) {
    var comune: ComuneVO = evento.item;
    this.selezionaComune(comune);
  }

  /**
   * Al blur del campo comune, se il valore digitato corrisponde esattamente
   * a uno dei risultati dell'ultima ricerca, lo seleziona automaticamente.
   * Ritardato di 250ms per permettere a (typeaheadOnSelect) di attivarsi
   * prima nel caso l'utente abbia cliccato su un item del dropdown.
   */
  onComuneBlur() {
    setTimeout(() => {
      if (this.comuneSelezionato) { return; }
      var valore = this.formBeneficiario.get('comune').value;
      if (!valore || typeof valore !== 'string') { return; }
      if (this.trySelectComuneByExactMatch(valore, this.ultimiComuniRicerca)) { return; }
      this.selezioneComunePendingOnBlur = true;
      if (this.ricercaComuneInCorso) {
        this.formBeneficiario.get('comune').updateValueAndValidity({ emitEvent: false });
        return;
      }
      this.ricercaComuneInCorso = true;
      this.anagrafeService.ricercaComune(valore).subscribe(
        (risultati) => {
          this.ultimiComuniRicerca = risultati || [];
          this.ricercaComuneInCorso = false;
          this.trySelectComuneByExactMatch(valore, this.ultimiComuniRicerca);
          this.selezioneComunePendingOnBlur = false;
          this.formBeneficiario.get('comune').updateValueAndValidity({ emitEvent: false });
        },
        () => {
          this.ricercaComuneInCorso = false;
          this.selezioneComunePendingOnBlur = false;
          this.formBeneficiario.get('comune').updateValueAndValidity({ emitEvent: false });
        }
      );
    }, 250);
  }

  private selezionaComune(comune: ComuneVO) {
    this.comuneSelezionato = true;
    this.ricercaComuneInCorso = false;
    this.selezioneComunePendingOnBlur = false;
    this.ultimiComuniRicerca = [];
    this.formBeneficiario.get('comune').setValue(comune.nome ? comune.nome.toUpperCase() : '', { emitEvent: false });
    this.formBeneficiario.get('provincia').setValue(comune.provincia);
    if (comune.cap) {
      this.formBeneficiario.get('cap').setValue(comune.cap);
    }
    this.formBeneficiario.get('comune').updateValueAndValidity({ emitEvent: false });
  }

  svuotaComune() {
    this.comuneSelezionato = false;
    this.ricercaComuneInCorso = false;
    this.selezioneComunePendingOnBlur = false;
    this.ultimiComuniRicerca = [];
    this.formBeneficiario.get('comune').setValue('');
    this.formBeneficiario.get('provincia').setValue('');
    this.formBeneficiario.get('cap').setValue('');
    this.formBeneficiario.get('comune').updateValueAndValidity({ emitEvent: false });
  }

  private trySelectComuneByExactMatch(valore: string, risultati: ComuneVO[]): boolean {
    if (!valore || typeof valore !== 'string' || !risultati || !risultati.length) {
      return false;
    }

    var valoreUpper = valore.toUpperCase().trim();
    for (var i = 0; i < risultati.length; i++) {
      var comune = risultati[i];
      if (comune && comune.nome && comune.nome.toUpperCase().trim() === valoreUpper) {
        this.selezionaComune(comune);
        return true;
      }
    }

    return false;
  }

  capItalianoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const value = control.value.toString().replace(/\s/g, '');

      // CAP italiano: 5 cifre, prima cifra da 0 a 9
      const capPattern = /^[0-9]{5}$/;

      if (!capPattern.test(value)) {
        return {
          capInvalid: {
            message: 'Inserire un CAP italiano valido (5 cifre)'
          }
        };
      }

      return null;
    };
  }

  ibanValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const value = control.value.toString().toUpperCase().replace(/\s/g, '');

      // Pattern IBAN base (2 lettere + 2 cifre + fino a 30 caratteri alfanumerici)
      const ibanPattern = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/;

      if (!ibanPattern.test(value)) {
        return {
          ibanInvalid: {
            message: 'Formato IBAN non valido'
          }
        };
      }

      // Validazione algoritmo MOD-97 per IBAN
      if (!this.validateIbanChecksum(value)) {
        return {
          ibanInvalid: {
            message: 'IBAN non valido (errore checksum)'
          }
        };
      }

      return null;
    };
  }

  // Algoritmo di validazione checksum IBAN (MOD-97)
  private validateIbanChecksum(iban: string): boolean {
    // Sposta i primi 4 caratteri alla fine
    const rearranged = iban.substring(4) + iban.substring(0, 4);

    // Converti lettere in numeri (A=10, B=11, ..., Z=35)
    let numericString = '';
    for (let i = 0; i < rearranged.length; i++) {
      const char = rearranged.charAt(i);
      if (char >= 'A' && char <= 'Z') {
        numericString += (char.charCodeAt(0) - 'A'.charCodeAt(0) + 10).toString();
      } else {
        numericString += char;
      }
    }

    // Calcola MOD 97
    let remainder = 0;
    for (let i = 0; i < numericString.length; i++) {
      remainder = (remainder * 10 + parseInt(numericString.charAt(i))) % 97;
    }

    return remainder === 1;
  }

  // Validatore per il formato del conto (8 cifre numeriche)
  contoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Se il campo è vuoto, lascia che required si occupi della validazione
      }

      const value = control.value.toString();
      const isValid = /^\d{8}$/.test(value);

      return isValid ? null : {
        contoFormat: {
          message: 'Il conto deve essere composto da esattamente 8 cifre numeriche'
        }
      };
    };
  }

  // Validatore condizionale per liquidazioni contributi
  contoContributiValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || this.tipoNota !== TipoNotaLiquidazioneEnum.LIQUIDAZIONE_CONTRIBUTI) {
        return null;
      }

      const value = control.value.toString();
      const startsWithCorrectCode = value.startsWith('120');

      return startsWithCorrectCode ? null : {
        contoContributi: {
          message: 'Per le liquidazioni di contributi il conto deve iniziare con "120"'
        }
      };
    };
  }

  // Metodo per ottenere il messaggio di errore del conto
  getContoErrorMessage(): string {
    const control = this.formBeneficiario.get('classeContabile');

    if (control.hasError('required')) {
      return 'Il conto è obbligatorio';
    }

    if (control.hasError('contoFormat')) {
      return control.errors['contoFormat'].message;
    }

    if (control.hasError('contoContributi')) {
      return control.errors['contoContributi'].message;
    }

    return '';
  }

  // Metodo per aggiornare i validatori in base al tipo di soggetto
  updateValidators(): void {
    const tipoBeneficiarioControl = this.formBeneficiario.get('tipoBeneficiario');
    const codiceFiscaleControl = this.formBeneficiario.get('codiceFiscale');
    const partitaIvaControl = this.formBeneficiario.get('partitaIva');
    const ibanControl = this.formBeneficiario.get('iban');
    const ritenutaControl = this.formBeneficiario.get('ritenuta');

    if (!tipoBeneficiarioControl || !codiceFiscaleControl || !partitaIvaControl || !ibanControl || !ritenutaControl) {
      return;
    }

    const tipoBeneficiario = tipoBeneficiarioControl.value;

    switch (tipoBeneficiario) {
      case TipoBeneficiarioEnum.PERSONA_FISICA:
        // Persona fisica: solo CF obbligatorio (pattern alfanumerico)
        codiceFiscaleControl.setValidators([Validators.required, this.codiceFiscalePersonaFisicaValidator()]);
        partitaIvaControl.clearValidators();
        // Reset partita IVA se non più obbligatoria
        partitaIvaControl.reset();
        break;

      case TipoBeneficiarioEnum.DITTA_INDIVIDUALE:
        // Ditta individuale: CF e PI entrambi obbligatori (entrambi alfanumerici)
        codiceFiscaleControl.setValidators([Validators.required, this.codiceFiscalePersonaFisicaValidator()]);
        partitaIvaControl.setValidators([Validators.required, this.partitaIvaValidator()]);
        break;

      case TipoBeneficiarioEnum.PERSONA_GIURIDICA:
        // Persona giuridica: CF e PI entrambi obbligatori (entrambi numerici)
        codiceFiscaleControl.setValidators([Validators.required, this.codiceFiscaleSoggettoGiuridicoValidator()]);
        partitaIvaControl.setValidators([Validators.required, this.partitaIvaValidator()]);
        break;

      default:
        // Default: CF obbligatorio
        codiceFiscaleControl.setValidators([Validators.required]);
        partitaIvaControl.clearValidators();
        // Reset partita IVA se non più obbligatoria
        partitaIvaControl.reset();
        break;
    }

    // Aggiorna i validatori IBAN e ritenuta con reset se necessario
    const previousIbanRequired = this.isIbanRequired();
    const previousRitenutaVisible = this.isRitenutaVisible();

    ibanControl.setValidators(this.getIbanValidators());
    ritenutaControl.setValidators(this.getRitenutaValidators());

    // Reset dei campi se diventano non visibili/non obbligatori
    if (!this.isIbanVisible()) {
      ibanControl.reset();
    }
    if (!this.isRitenutaVisible()) {
      ritenutaControl.reset();
    }

    codiceFiscaleControl.updateValueAndValidity();
    partitaIvaControl.updateValueAndValidity();
    ibanControl.updateValueAndValidity();
    ritenutaControl.updateValueAndValidity();

    // Aggiorna anche i validatori della scadenza DURC per tutti i documenti
    this.updateScadenzaDurcValidators();
  }

  // Metodo per aggiornare i validatori della scadenza DURC per tutti i documenti
  private updateScadenzaDurcValidators(): void {
    for (let i = 0; i < this.documentiSpesaArray.length; i++) {
      const documentoGroup = this.documentiSpesaArray.at(i) as FormGroup;
      const scadenzaDurcControl = documentoGroup.get('scadenzaDurc') as FormControl;

      this.setScadenzaDurcValidatorsForDocument(scadenzaDurcControl);
    }
  }

  // Metodo helper per impostare i validatori della scadenza DURC per un singolo documento
  private setScadenzaDurcValidatorsForDocument(scadenzaDurcControl: FormControl): void {
    const tipoBeneficiario = this.formBeneficiario.get('tipoBeneficiario').value;

    if (tipoBeneficiario === TipoBeneficiarioEnum.PERSONA_FISICA) {
      // Persona fisica: scadenza DURC non richiesta e non visibile
      scadenzaDurcControl.setValidators(this.scadenzaDurcValidator());
      scadenzaDurcControl.reset();
    } else {
      // Ditta individuale e Persona giuridica: scadenza DURC obbligatoria.
      // Il validatore di data gestisce internamente le soglie per tipo nota
      // (fattura commerciale: solo non scaduta, senza soglia dei 10 giorni)
      scadenzaDurcControl.setValidators([Validators.required, this.scadenzaDurcValidator()]);
    }

    scadenzaDurcControl.updateValueAndValidity();
  }

  // Validatori per i nuovi campi separati
  codiceFiscalePersonaFisicaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const value = control.value.toString().toUpperCase().replace(/\s/g, '');
      const cfPattern = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;

      if (!cfPattern.test(value)) {
        return {
          codiceFiscalePersonaFisicaInvalid: {
            message: 'Inserire un Codice Fiscale valido per persona fisica (16 caratteri alfanumerici)'
          }
        };
      }

      return null;
    };
  }

  codiceFiscaleSoggettoGiuridicoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const value = control.value.toString().replace(/\s/g, '');
      const cfPattern = /^[0-9]{11}$/;

      if (!cfPattern.test(value)) {
        return {
          codiceFiscaleSoggettoGiuridicoInvalid: {
            message: 'Inserire un Codice Fiscale valido per soggetto giuridico (11 cifre numeriche)'
          }
        };
      }

      return null;
    };
  }

  partitaIvaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const value = control.value.toString().replace(/\s/g, '');
      const piPattern = /^[0-9]{11}$/;

      if (!piPattern.test(value)) {
        return {
          partitaIvaInvalid: {
            message: 'Inserire una Partita IVA valida (11 cifre numeriche)'
          }
        };
      }

      return null;
    };
  }

  // Metodo per ottenere la label dinamica per il campo denominazione
  getLabelDenominazione(): string {
    const tipoBeneficiarioControl = this.formBeneficiario.get('tipoBeneficiario');

    if (!tipoBeneficiarioControl) {
      return 'Nome e cognome / Ragione sociale *';
    }

    const tipoBeneficiario = tipoBeneficiarioControl.value;

    if (tipoBeneficiario === TipoBeneficiarioEnum.PERSONA_FISICA) {
      return 'Nome e cognome *';
    } else {
      return 'Ragione sociale *';
    }
  }

  // Metodo per ottenere il placeholder dinamico per il campo denominazione
  getDenominazionePlaceholder(): string {
    const tipoBeneficiarioControl = this.formBeneficiario.get('tipoBeneficiario');

    if (!tipoBeneficiarioControl || !tipoBeneficiarioControl.value) {
      return 'Digita nome e cognome o ragione sociale';
    }

    const tipoBeneficiario = tipoBeneficiarioControl.value;

    if (tipoBeneficiario === TipoBeneficiarioEnum.PERSONA_FISICA) {
      return 'Digita nome e cognome';
    } else {
      return 'Digita ragione sociale';
    }
  }

  // Metodo per determinare se il campo Scadenza DURC deve essere visibile
  isScadenzaDurcVisible(): boolean {
    const tipoBeneficiarioControl = this.formBeneficiario.get('tipoBeneficiario');

    if (!tipoBeneficiarioControl || !tipoBeneficiarioControl.value) {
      return false;
    }

    const tipoBeneficiario = tipoBeneficiarioControl.value;

    // Visibile solo per persona giuridica e ditta individuale, non per persona fisica
    return tipoBeneficiario !== TipoBeneficiarioEnum.PERSONA_FISICA;
  }

  // Metodo per determinare se la Partita IVA è obbligatoria
  isPartitaIvaRequired(): boolean {
    const tipoBeneficiarioControl = this.formBeneficiario.get('tipoBeneficiario');

    if (!tipoBeneficiarioControl || !tipoBeneficiarioControl.value) {
      return false;
    }

    const tipoBeneficiario = tipoBeneficiarioControl.value;

    // Obbligatoria per persona giuridica e ditta individuale, non per persona fisica
    return tipoBeneficiario !== TipoBeneficiarioEnum.PERSONA_FISICA;
  }

  // Metodo per ottenere il messaggio di errore del Codice Fiscale
  getCodiceFiscaleErrorMessage(): string {
    const control = this.formBeneficiario.get('codiceFiscale');

    if (control.hasError('required')) {
      return 'Il Codice Fiscale è obbligatorio';
    }

    if (control.hasError('codiceFiscalePersonaFisicaInvalid')) {
      return control.errors['codiceFiscalePersonaFisicaInvalid'].message;
    }

    if (control.hasError('codiceFiscaleSoggettoGiuridicoInvalid')) {
      return control.errors['codiceFiscaleSoggettoGiuridicoInvalid'].message;
    }

    return '';
  }

  // Metodo per ottenere il messaggio di errore della Partita IVA
  getPartitaIvaErrorMessage(): string {
    const control = this.formBeneficiario.get('partitaIva');

    if (control.hasError('required')) {
      return 'La Partita IVA è obbligatoria per i soggetti giuridici';
    }

    if (control.hasError('partitaIvaInvalid')) {
      return control.errors['partitaIvaInvalid'].message;
    }

    return '';
  }

  // Metodo per ottenere il messaggio di errore della linea di finanziamento per un documento specifico
  getLineaFinanziamentoErrorMessage(documentoIndex: number): string {
    const documentoGroup = this.documentiSpesaArray.at(documentoIndex) as FormGroup;
    const control = documentoGroup.get('lineaFinanziamento');

    if (control.hasError('required')) {
      return 'La linea di finanziamento è obbligatoria';
    }

    if (control.hasError('lineaFinanziamentoDiversa')) {
      return control.errors['lineaFinanziamentoDiversa'].message;
    }

    return '';
  }

  // Metodo per ottenere il messaggio di errore del CIG per un documento specifico
  getCigErrorMessage(documentoIndex: number): string {
    const documentoGroup = this.documentiSpesaArray.at(documentoIndex) as FormGroup;
    const control = documentoGroup.get('cig');

    if (control.hasError('required')) {
      return 'Il CIG è obbligatorio';
    }

    if (control.hasError('cigInvalid')) {
      return control.errors['cigInvalid'].message;
    }

    return '';
  }

  // Metodo per ottenere il messaggio di errore del CUP per un documento specifico
  getCupErrorMessage(documentoIndex: number): string {
    const documentoGroup = this.documentiSpesaArray.at(documentoIndex) as FormGroup;
    const control = documentoGroup.get('cup');

    if (control.hasError('required')) {
      return 'Il CUP è obbligatorio';
    }

    if (control.hasError('cupInvalid')) {
      return control.errors['cupInvalid'].message;
    }

    return '';
  }

  // Metodo per ottenere il messaggio di errore della Scadenza DURC per un documento specifico
  getDurcErrorMessage(documentoIndex: number): string {
    const documentoGroup = this.documentiSpesaArray.at(documentoIndex) as FormGroup;
    const control = documentoGroup.get('scadenzaDurc');

    if (control.hasError('required')) {
      return 'La Scadenza DURC è obbligatoria per i soggetti giuridici';
    }

    if (control.hasError('dataInvalida')) {
      return control.errors['dataInvalida'].message;
    }

    return '';
  }

  // Metodo per ottenere la data minima per la scadenza DURC
  getMinDateForDurc(): Date {
    if (this.tipoNota === TipoNotaLiquidazioneEnum.ALTRO) {
      // Per ALTRO, deve essere successiva alla data di oggi
      return new Date();
    }
    if (this.tipoNota === TipoNotaLiquidazioneEnum.LIQUIDAZIONE_FATTURA_COMMERCIALE) {
      // Nessuna soglia dei 10 giorni, ma non è selezionabile una data già passata
      return new Date();
    } else if ( this.tipoNota === TipoNotaLiquidazioneEnum.LIQUIDAZIONE_CONTRIBUTI ||
      this.tipoNota === TipoNotaLiquidazioneEnum.PAGAMENTO_IMPOSTE) {
      // Per fatture commerciali, liquidazione contributi e pagamento imposte, deve essere successiva di almeno 10 giorni
      const dataMinima = new Date();
      dataMinima.setDate(dataMinima.getDate() + 10);
      return dataMinima;
    }
    // Per altri tipi di nota, nessuna restrizione di data minima
    return null;
  }

  // Metodo per creare un singolo FormGroup per documento di spesa
  private createDocumentoSpesaFormGroup(): FormGroup {
    return this.fb.group({
      importo: ["", Validators.required],
      importoNetto: [""],
      iva: [""],
      soggettoIva: [true, Validators.required],
      splitPayment: [false, Validators.required],
      pad: [""],
      cig: ["", this.cigValidator()],
      cup: ["", this.cupValidator()],
      lineaFinanziamento: ["", [Validators.required, this.lineaFinanziamentoUnicaValidator()]],
      scadenzaDurc: ["", this.scadenzaDurcValidator()]
    });
  }

  // Getter per ottenere il FormArray dei documenti di spesa
  get documentiSpesaArray(): FormArray {
    return this.formBeneficiario.get('documentiSpesa') as FormArray;
  }

  // Metodo per aggiungere un nuovo documento di spesa
  aggiungiDocumentoSpesa(): void {
    const nuovoDocumento = this.createDocumentoSpesaFormGroup();
    this.documentiSpesaArray.push(nuovoDocumento);

    // Applica le logiche di validazione al nuovo documento
    this.applicaLogicheValidazioneDocumento(this.documentiSpesaArray.length - 1);

    this.cd.detectChanges();
  }

  // Metodo per applicare le logiche di validazione a un documento specifico
  private applicaLogicheValidazioneDocumento(index: number): void {
    const documentoGroup = this.documentiSpesaArray.at(index) as FormGroup;

    // Ottieni i controlli del documento
    const padControl = documentoGroup.get('pad') as FormControl;
    const cigControl = documentoGroup.get('cig') as FormControl;
    const cupControl = documentoGroup.get('cup') as FormControl;
    const lineaFinanziamentoControl = documentoGroup.get('lineaFinanziamento') as FormControl;
    const importoControl = documentoGroup.get('importo') as FormControl;
    const importoNettoControl = documentoGroup.get('importoNetto') as FormControl;
    const ivaControl = documentoGroup.get('iva') as FormControl;
    const soggettoIvaControl = documentoGroup.get('soggettoIva') as FormControl;
    const splitPaymentControl = documentoGroup.get('splitPayment') as FormControl;
    const scadenzaDurcControl = documentoGroup.get('scadenzaDurc') as FormControl;

    // Applica validazioni basate sul tipo di nota
    if (this.tipoNota === TipoNotaLiquidazioneEnum.LIQUIDAZIONE_FATTURA_COMMERCIALE) {
      // Per fattura commerciale soggettoIva è true di default, ma NON va forzato:
      // l'utente può deselezionarlo per fatture esenti IVA e la scelta deve
      // essere preservata alla riapertura del modale (es. in clonazione)
      if (soggettoIvaControl.value === null || soggettoIvaControl.value === undefined) {
        soggettoIvaControl.setValue(true);
      }
      padControl.setValidators(Validators.required);
      // IVA e netto obbligatori solo se il pagamento è soggetto a IVA;
      // i cambi successivi sono gestiti dalla subscription su soggettoIva
      if (soggettoIvaControl.value === true) {
        ivaControl.setValidators(Validators.required);
        importoNettoControl.setValidators(Validators.required);
      }
      cigControl.setValidators(this.cigValidator());
    } else if (this.tipoNota === TipoNotaLiquidazioneEnum.LIQUIDAZIONE_CONTRIBUTI) {
      // Per liquidazione contributi: IVA sempre a 0 e campo disabilitato
      soggettoIvaControl.setValue(false);
      ivaControl.setValue(0);
      ivaControl.disable();
      // Reset importoNetto per evitare che venga salvato
      importoNettoControl.setValue(null);
      importoNettoControl.clearValidators();
      // Assicurati che il campo importo sia obbligatorio per liquidazione contributi
      importoControl.setValidators(Validators.required);
      padControl.clearValidators();
      cigControl.setValidators(this.cigValidator());
    } else if (this.tipoNota === TipoNotaLiquidazioneEnum.ALTRO) {
      // Per tipologia ALTRO: nessun calcolo IVA, importo diretto
      soggettoIvaControl.setValue(false);
      ivaControl.clearValidators();
      importoNettoControl.clearValidators();
      // Assicurati che il campo importo sia obbligatorio per ALTRO
      importoControl.setValidators(Validators.required);
      padControl.clearValidators();
      cigControl.setValidators(this.cigValidator());
    } else if (this.tipoNota === TipoNotaLiquidazioneEnum.PAGAMENTO_IMPOSTE) {
      // Per pagamento imposte: nessun calcolo IVA, importo diretto
      soggettoIvaControl.setValue(false);
      ivaControl.clearValidators();
      importoNettoControl.clearValidators();
      // Assicurati che il campo importo sia obbligatorio per PAGAMENTO_IMPOSTE
      importoControl.setValidators(Validators.required);
      // PAD e CIG non visibili per pagamento imposte
      padControl.clearValidators();
      cigControl.clearValidators();
    } else {
      // Solo imposta soggettoIva a false se non ha già un valore (nuovo documento)
      // Preserva il valore esistente durante l'editing
      if (soggettoIvaControl.value === null || soggettoIvaControl.value === undefined) {
        soggettoIvaControl.setValue(false);
      }
      padControl.clearValidators();
      ivaControl.clearValidators();
      cigControl.setValidators(this.cigValidator()); // Mantiene sempre il validatore personalizzato del CIG
      importoNettoControl.clearValidators();
    }

    // Funzione per ricalcolare l'importo totale per questo documento
    const ricalcolaImportoTotale = (forzaRicalcolo: boolean = false) => {
      console.log('[DEBUG ricalcolaImportoTotale] chiamata con forzaRicalcolo:', forzaRicalcolo);
      console.log('[DEBUG ricalcolaImportoTotale] tipoNota:', this.tipoNota);

      // Skip per tipi di nota che non usano calcolo IVA (importo inserito direttamente)
      if (this.tipoNota === TipoNotaLiquidazioneEnum.LIQUIDAZIONE_CONTRIBUTI ||
        this.tipoNota === TipoNotaLiquidazioneEnum.PAGAMENTO_IMPOSTE ||
        this.tipoNota === TipoNotaLiquidazioneEnum.ALTRO) {
        console.log('[DEBUG ricalcolaImportoTotale] SKIP: tipo nota senza calcolo IVA');
        return;
      }

      // Verifica che soggettoIva sia true prima di ricalcolare
      const soggettoIva = soggettoIvaControl.value;
      console.log('[DEBUG ricalcolaImportoTotale] soggettoIva:', soggettoIva);
      if (!soggettoIva && !forzaRicalcolo) {
        console.log('[DEBUG ricalcolaImportoTotale] SKIP: soggettoIva false e non forzato');
        return;
      }

      const importoNetto = importoNettoControl.value;
      const iva = ivaControl.value;
      console.log('[DEBUG ricalcolaImportoTotale] importoNetto:', importoNetto, 'iva:', iva);

      // Verifica che entrambi i valori siano presenti e validi
      const importoNettoValido = importoNetto !== null && importoNetto !== undefined && importoNetto !== '' && !isNaN(parseFloat(importoNetto));
      const ivaValida = iva !== null && iva !== undefined && iva !== '' && !isNaN(parseFloat(iva.toString()));
      console.log('[DEBUG ricalcolaImportoTotale] importoNettoValido:', importoNettoValido, 'ivaValida:', ivaValida);

      if (importoNettoValido && ivaValida) {
        const importoNettoNum = parseFloat(importoNetto.toString());
        const ivaNum = parseFloat(iva.toString());
        const importoTotale = importoNettoNum + (importoNettoNum * ivaNum / 100);
        // Arrotonda a 2 decimali per evitare problemi di precisione floating point
        const importoTotaleArrotondato = Math.round(importoTotale * 100) / 100;
        console.log('[DEBUG ricalcolaImportoTotale] CALCOLO:', importoNettoNum, '+', ivaNum, '% =', importoTotaleArrotondato);
        importoControl.setValue(importoTotaleArrotondato, { emitEvent: false });
      } else {
        console.log('[DEBUG ricalcolaImportoTotale] SKIP: valori non validi');
      }
    };

    // Sottoscrizioni per questo documento specifico
    importoNettoControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => ricalcolaImportoTotale());

    ivaControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => ricalcolaImportoTotale(true));

    // Stash di netto/IVA per ripristinarli se l'utente riattiva soggettoIva
    // dopo averlo spento (lo spegnimento azzera importoNetto e IVA)
    let importoNettoStash = null;
    let ivaStash = null;

    soggettoIvaControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res === true) {
          importoNettoControl.setValidators(Validators.required);
          ivaControl.setValidators(Validators.required);
          splitPaymentControl.setValidators(Validators.required);
          // Ripristina netto/IVA azzerati allo spegnimento, solo se l'utente
          // non li ha nel frattempo reinseriti manualmente
          const importoNettoCorrente = importoNettoControl.value;
          if ((importoNettoCorrente === null || importoNettoCorrente === undefined || importoNettoCorrente === '') && importoNettoStash !== null) {
            importoNettoControl.setValue(importoNettoStash, { emitEvent: false });
          }
          const ivaCorrente = ivaControl.value;
          if ((ivaCorrente === null || ivaCorrente === undefined || ivaCorrente === '' || parseFloat(ivaCorrente.toString()) === 0) && ivaStash !== null) {
            ivaControl.setValue(ivaStash, { emitEvent: false });
          }
          importoNettoStash = null;
          ivaStash = null;
          // IMPORTANTE: Forza il ricalcolo quando si attiva soggettoIva
          // per aggiornare l'importo se importoNetto e iva sono già compilati
          ricalcolaImportoTotale(true);
        } else {
          importoNettoControl.clearValidators();
          ivaControl.clearValidators();
          splitPaymentControl.clearValidators();
          // Memorizza netto/IVA prima di azzerarli, per poterli ripristinare
          const ivaDaSalvare = ivaControl.value;
          ivaStash = (ivaDaSalvare !== null && ivaDaSalvare !== undefined && ivaDaSalvare !== '' && parseFloat(ivaDaSalvare.toString()) !== 0) ? ivaDaSalvare : null;
          // Quando soggettoIva diventa false:
          // - Setta IVA a 0 per aggiornare correttamente il riepilogo
          // - Se importoNetto ha un valore, imposta importo = importoNetto (senza IVA)
          ivaControl.setValue(0, { emitEvent: false });
          const importoNettoValue = importoNettoControl.value;
          if (importoNettoValue !== null && importoNettoValue !== undefined && importoNettoValue !== '' && !isNaN(parseFloat(importoNettoValue))) {
            importoNettoStash = importoNettoValue;
            importoControl.setValue(parseFloat(importoNettoValue.toString()), { emitEvent: false });
          }
          // Azzera importoNetto: senza IVA il documento usa solo "importo".
          // Un valore residuo (es. da nota clonata) falserebbe i riepiloghi
          // totali, che danno priorità a importoNetto quando valorizzato.
          importoNettoControl.setValue(null, { emitEvent: false });
          if (splitPaymentControl.value === null || splitPaymentControl.value === undefined) {
            splitPaymentControl.reset();
          }
        }
        importoNettoControl.updateValueAndValidity();
        ivaControl.updateValueAndValidity();
        splitPaymentControl.updateValueAndValidity();
      });

    lineaFinanziamentoControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res === LineeFinanziamentoEnum.FSE) {
          padControl.setValidators(Validators.required);
        } else {
          padControl.clearValidators();
          // Non resettare PAD durante l'editing se ha già un valore
          // Il reset viene fatto solo per nuovi documenti
          if (!padControl.value) {
            padControl.reset();
          }
        }

        if (res === LineeFinanziamentoEnum.ALTRO) {
          cupControl.setValidators(this.cupValidator()); // Solo validatore personalizzato, non obbligatorio
          // Non resettare CUP durante l'editing se ha già un valore
          // Il reset viene fatto solo per nuovi documenti
          if (!cupControl.value) {
            cupControl.reset();
          }
        } else {
          cupControl.setValidators([Validators.required, this.cupValidator()]); // Obbligatorio + validatore personalizzato
        }

        // Per Liquidazione contributi + L. 68/1999 o ALTRO: CIG non obbligatorio (nascosto), CUP obbligatorio
        if ((res === LineeFinanziamentoEnum.LEGGE681999 || res === LineeFinanziamentoEnum.ALTRO) && this.tipoNota === TipoNotaLiquidazioneEnum.LIQUIDAZIONE_CONTRIBUTI) {
          cigControl.clearValidators();
          cigControl.reset();
          cupControl.setValidators([Validators.required, this.cupValidator()]);
        }
        cigControl.updateValueAndValidity();

        padControl.updateValueAndValidity();
        cupControl.updateValueAndValidity();

        // Rivalidare tutti gli altri documenti quando cambia la linea di finanziamento
        this.rivalidaTuttiDocumenti();
      });

    // Imposta validatori per scadenza DURC in base al tipo di persona
    this.setScadenzaDurcValidatorsForDocument(scadenzaDurcControl);

    // Applica manualmente la logica di validazione basata sulla linea di finanziamento
    // per gestire il caso in cui il valore è già impostato durante l'editing
    const currentLineaFinanziamento = lineaFinanziamentoControl.value;
    if (currentLineaFinanziamento) {
      if (currentLineaFinanziamento === LineeFinanziamentoEnum.FSE) {
        padControl.setValidators(Validators.required);
      } else {
        padControl.clearValidators();
        // Non resettare PAD durante l'editing se ha già un valore
        if (!padControl.value) {
          padControl.reset();
        }
      }

      if (currentLineaFinanziamento === LineeFinanziamentoEnum.ALTRO) {
        cupControl.setValidators(this.cupValidator()); // Solo validatore personalizzato, non obbligatorio
      } else {
        cupControl.setValidators([Validators.required, this.cupValidator()]); // Obbligatorio + validatore personalizzato
      }

      // Per Liquidazione contributi + L. 68/1999 o ALTRO: CIG non obbligatorio (nascosto), CUP obbligatorio
      if ((currentLineaFinanziamento === LineeFinanziamentoEnum.LEGGE681999 || currentLineaFinanziamento === LineeFinanziamentoEnum.ALTRO) && this.tipoNota === TipoNotaLiquidazioneEnum.LIQUIDAZIONE_CONTRIBUTI) {
        cigControl.clearValidators();
        cupControl.setValidators([Validators.required, this.cupValidator()]);
      }
      cigControl.updateValueAndValidity();
    }

    // Ricalcola l'importo totale se soggettoIva è già true (caso editing)
    // Questo è necessario perché le subscription sono state create dopo che i valori erano già settati
    // e quindi valueChanges non scatta per i valori già presenti
    if (soggettoIvaControl.value === true) {
      ricalcolaImportoTotale(true);
    }

    // Aggiorna la validità di tutti i controlli
    Object.keys(documentoGroup.controls).forEach(key => {
      documentoGroup.get(key).updateValueAndValidity({ emitEvent: false });
    });
  }

  // Metodo per rimuovere un documento di spesa
  rimuoviDocumentoSpesa(index: number): void {
    if (this.documentiSpesaArray.length > 1) {
      this.documentiSpesaArray.removeAt(index);
      // Rivalidare tutti i documenti rimasti dopo la rimozione
      this.rivalidaTuttiDocumenti();
      this.cd.detectChanges();
    }
  }

  // Metodo per rivalidare tutti i documenti di spesa
  private rivalidaTuttiDocumenti(): void {
    if (!this.documentiSpesaArray) {
      return;
    }

    // Cancella il timeout precedente se esiste
    if (this.revalidationTimeout) {
      clearTimeout(this.revalidationTimeout);
    }

    // Usa setTimeout per evitare problemi con il change detection durante il valueChanges
    this.revalidationTimeout = setTimeout(() => {
      // Verifica che la modale sia ancora aperta e i documenti esistano
      if (!this.modaleAperta || !this.documentiSpesaArray) {
        return;
      }

      for (let i = 0; i < this.documentiSpesaArray.length; i++) {
        const documentoGroup = this.documentiSpesaArray.at(i) as FormGroup;
        const lineaFinanziamentoControl = documentoGroup.get('lineaFinanziamento');
        if (lineaFinanziamentoControl) {
          // Rivalidare il campo - i validatori verranno rieseguiti con i valori correnti
          lineaFinanziamentoControl.updateValueAndValidity();
          // Marca il campo come touched per mostrare gli errori
          lineaFinanziamentoControl.markAsTouched();
        }
      }

      // Forza il change detection solo se la modale è ancora aperta
      if (this.modaleAperta) {
        this.cd.detectChanges();
      }

      // Pulisci il riferimento al timeout
      this.revalidationTimeout = null;
    }, 0);
  }

  // Metodo per ottenere il riepilogo dei totali (Lordo, Netto, Ritenute)
  getRiepilogoTotali(): { importoLordo: number, ritenute: number, importoNetto: number; } {
    let importoLordoTotale = 0;
    let importoNettoTotale = 0;
    let ritenuteTotali = 0;
    const ritenutaPercentuale = parseFloat(this.formBeneficiario.get('ritenuta').value || '0');

    for (let i = 0; i < this.documentiSpesaArray.length; i++) {
      const documentoGroup = this.documentiSpesaArray.at(i);
      const importo = parseFloat(documentoGroup.get('importo').value || '0');
      const importoNetto = parseFloat(documentoGroup.get('importoNetto').value || '0');

      let nettoDaLiquidare = 0;

      // Il netto è la somma degli importi senza IVA
      if (importoNetto > 0) {
        nettoDaLiquidare = importoNetto;
      } else {
        // Se non c'è importo netto (es. contributi), il netto coincide con l'importo
        nettoDaLiquidare = importo;
      }

      importoNettoTotale += nettoDaLiquidare;

      // Calcolo inverso: Netto = Lordo - (Lordo * %Ritenuta)
      // Lordo = Netto / (1 - %Ritenuta)
      let imponibile = nettoDaLiquidare;

      // Ritenuta calcolata sulla base imponibile (input)
      let ritenuta = 0;
      if (ritenutaPercentuale > 0) {
        ritenuta = (imponibile * ritenutaPercentuale) / 100;
        ritenuteTotali += ritenuta;
      }

      // Calcolo IVA su Imponibile
      const ivaPercentuale = parseFloat(documentoGroup.get('iva').value || '0');
      let iva = 0;
      if (ivaPercentuale > 0) {
        iva = (imponibile * ivaPercentuale) / 100;
      }

      // Lordo Totale = Imponibile + IVA
      importoLordoTotale += (imponibile + iva);
    }

    const importoNettoFinale = importoLordoTotale - ritenuteTotali;

    return {
      importoLordo: importoLordoTotale,
      ritenute: ritenuteTotali,
      importoNetto: importoNettoFinale
    };
  }

  // Metodo per ottenere il totale di tutti i documenti
  get importoTotaleDocumenti(): number {
    let totale = 0;
    for (let i = 0; i < this.documentiSpesaArray.length; i++) {
      const importo = this.documentiSpesaArray.at(i).get('importo').value;
      if (importo && !isNaN(importo)) {
        totale += parseFloat(importo);
      }
    }
    return totale;
  }

  // Metodo per ottenere le linee di finanziamento filtrate in base al tipo di nota
  get lineeFinanziamentoFiltrate(): LineeFinanziamentoEnum[] {
    if (this.tipoNota === TipoNotaLiquidazioneEnum.PAGAMENTO_IMPOSTE) {
      // Per pagamento imposte mostra solo ALTRO
      return [LineeFinanziamentoEnum.ALTRO];
    }

    if (this.tipoNota === TipoNotaLiquidazioneEnum.LIQUIDAZIONE_CONTRIBUTI) {
      // Per le liquidazioni contributi mostra tutte le linee di finanziamento
      return this.lineeFinanziamento;
    }

    // Per fatture commerciali e altri tipi mostra solo FSE, PNRR e ALTRO
    return [
      LineeFinanziamentoEnum.FSE,
      LineeFinanziamentoEnum.PNRR,
      LineeFinanziamentoEnum.ALTRO
    ];
  }

  // Metodo per ottenere la label della linea di finanziamento condizionata al tipo di nota
  getLineaFinanziamentoLabel(item: LineeFinanziamentoEnum): string {
    if (item === LineeFinanziamentoEnum.PNRR && this.tipoNota === TipoNotaLiquidazioneEnum.LIQUIDAZIONE_FATTURA_COMMERCIALE) {
      return 'PNRR E D.M. 74/2019';
    }
    return LineeFinanziamentoLabelMapping[item] || String(item);
  }

  // Metodo per ottenere i tipi di beneficiario filtrati in base al tipo di nota
  get tipiBeneficiarioFiltrati(): TipoBeneficiarioEnum[] {
    if (this.tipoNota === TipoNotaLiquidazioneEnum.PAGAMENTO_IMPOSTE) {
      // Per pagamento imposte nascondi PERSONA_FISICA
      return this.tipiBeneficiario.filter(tipo => tipo !== TipoBeneficiarioEnum.PERSONA_FISICA);
    }

    // Per tutti gli altri tipi mostra tutti i beneficiari
    return this.tipiBeneficiario;
  }

  // Metodo per popolare il form in caso di modifica di un beneficiario esistente
  private popolaFormPerModifica(formBeneficiarioEdit: FormGroup): void {
    // getRawValue() e non .value: .value esclude i controlli disabled
    // (es. provincia, compilata via typeahead comune), che altrimenti
    // andrebbero persi a ogni riapertura del modale in modifica
    const valoriEdit = formBeneficiarioEdit.getRawValue();

    // Se il comune è già compilato, segna come selezionato
    if (valoriEdit.comune) {
      this.comuneSelezionato = true;
    }

    // Popola i campi dell'anagrafica beneficiario
    this.formBeneficiario.patchValue({
      tipoBeneficiario: valoriEdit.tipoBeneficiario, // Campo mancante aggiunto
      personaFisica: valoriEdit.personaFisica,
      denominazione: valoriEdit.denominazione,
      codiceFiscale: valoriEdit.codiceFiscale,
      partitaIva: valoriEdit.partitaIva,
      indirizzo: valoriEdit.indirizzo,
      comune: valoriEdit.comune,
      provincia: valoriEdit.provincia,
      cap: valoriEdit.cap,
      classeContabile: valoriEdit.classeContabile,
      iban: valoriEdit.iban,
      ritenuta: valoriEdit.ritenuta,
      protocolliDomanda: valoriEdit.protocolliDomanda,
      codiceCor: valoriEdit.codiceCor,
      noteAggiuntive: valoriEdit.noteAggiuntive
    });

    // Gestisce i documenti di spesa
    if (valoriEdit.documentiSpesa && Array.isArray(valoriEdit.documentiSpesa)) {
      // Se ci sono più documenti nell'edit, rimuovi il documento vuoto iniziale e aggiungi quelli dell'edit
      this.documentiSpesaArray.clear();

      valoriEdit.documentiSpesa.forEach((documento: any, index: number) => {
        const documentoGroup = this.createDocumentoSpesaFormGroup();

        // Pulisci campi con stringhe vuote prima del patch
        const documentoPulito = { ...documento };
        Object.keys(documentoPulito).forEach(key => {
          if (documentoPulito[key] === '' || documentoPulito[key] === undefined) {
            documentoPulito[key] = null;
          }
        });

        documentoGroup.patchValue(documentoPulito);

        // Se il documento ha un valore IVA > 0, imposta soggettoIva a true automaticamente
        // Per IVA = 0, preserva il valore originale (potrebbe essere zero-rated VAT)
        if (documento.iva && documento.iva > 0) {
          documentoGroup.get('soggettoIva').setValue(true);
        }
        // Non forzare soggettoIva = false per iva = 0, preserva il valore originale

        this.documentiSpesaArray.push(documentoGroup);

        console.log('[DEBUG popolaFormPerModifica] PRIMA di applicaLogicheValidazioneDocumento:', {
          importo: documentoGroup.get('importo').value,
          importoNetto: documentoGroup.get('importoNetto').value,
          iva: documentoGroup.get('iva').value,
          soggettoIva: documentoGroup.get('soggettoIva').value
        });

        // Applica le logiche di validazione a ogni documento
        this.applicaLogicheValidazioneDocumento(index);

        console.log('[DEBUG popolaFormPerModifica] DOPO applicaLogicheValidazioneDocumento:', {
          importo: documentoGroup.get('importo').value,
          importoNetto: documentoGroup.get('importoNetto').value,
          iva: documentoGroup.get('iva').value,
          soggettoIva: documentoGroup.get('soggettoIva').value
        });

        // Forza l'aggiornamento della validità del documento dopo la configurazione
        documentoGroup.updateValueAndValidity();
      });
    } else if (valoriEdit.documentoSpesa) {
      // Compatibilità con la vecchia struttura (singolo documento)
      const primoDocumento = this.documentiSpesaArray.at(0) as FormGroup;

      // Pulisci campi con stringhe vuote prima del patch
      const documentoPulito = { ...valoriEdit.documentoSpesa };
      Object.keys(documentoPulito).forEach(key => {
        if (documentoPulito[key] === '' || documentoPulito[key] === undefined) {
          documentoPulito[key] = null;
        }
      });

      primoDocumento.patchValue(documentoPulito);

      // Se il documento ha un valore IVA > 0, imposta soggettoIva a true automaticamente
      // Per IVA = 0, preserva il valore originale (potrebbe essere zero-rated VAT)
      if (valoriEdit.documentoSpesa.iva && valoriEdit.documentoSpesa.iva > 0) {
        primoDocumento.get('soggettoIva').setValue(true);
      }
      // Non forzare soggettoIva = false per iva = 0, preserva il valore originale

      this.applicaLogicheValidazioneDocumento(0);

      // Forza l'aggiornamento della validità del documento dopo la configurazione
      primoDocumento.updateValueAndValidity();
    }

    // Aggiorna i validatori dopo aver popolato il form
    // Rimuovi setTimeout per rendere l'operazione sincrona
    this.updateValidators();

    // NOTA: applicaLogicheValidazioneDocumento è già stato chiamato per ogni documento
    // sopra (riga 1210 per array, riga 1236 per singolo documento).
    // Non richiamare qui per evitare subscription duplicate e ricalcoli multipli.

    // Forza l'aggiornamento della validità del form
    this.formBeneficiario.updateValueAndValidity();

    // Marca tutti i campi come "touched" per mostrare gli errori di validazione immediatamente
    Object.keys(this.formBeneficiario.controls).forEach(key => {
      this.formBeneficiario.get(key).markAsTouched();
    });

    // Marca anche i campi dei documenti di spesa come "touched"
    for (let i = 0; i < this.documentiSpesaArray.length; i++) {
      const documentoGroup = this.documentiSpesaArray.at(i) as FormGroup;
      Object.keys(documentoGroup.controls).forEach(key => {
        documentoGroup.get(key).markAsTouched();
      });
    }

    // Debug: mostra eventuali errori di validazione
    console.log('[ModaleAggiungiBeneficiario] Form validity after populate:', this.formBeneficiario.valid);
    if (!this.formBeneficiario.valid) {
      console.log('[ModaleAggiungiBeneficiario] Form errors after populate:', this.getFormValidationErrors());
    }
  }

  // Helper method per debug della validazione form
  private getFormValidationErrors(): any {
    const formErrors: any = {};

    Object.keys(this.formBeneficiario.controls).forEach(key => {
      const controlErrors = this.formBeneficiario.get(key).errors;
      if (controlErrors) {
        formErrors[key] = controlErrors;
      }
    });

    // Controllo errori nei documenti di spesa
    const documentiArray = this.formBeneficiario.get("documentiSpesa") as FormArray;
    if (documentiArray) {
      documentiArray.controls.forEach((documentoGroup, index) => {
        const formGroup = documentoGroup as FormGroup;
        Object.keys(formGroup.controls).forEach(key => {
          const controlErrors = formGroup.get(key).errors;
          if (controlErrors) {
            if (!formErrors[`documento${index}`]) {
              formErrors[`documento${index}`] = {};
            }
            formErrors[`documento${index}`][key] = controlErrors;
          }
        });
      });
    }

    return formErrors;
  }

  // Validatore per il campo CIG (10 caratteri alfanumerici)
  cigValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Se il campo è vuoto, non è obbligatorio per default
      }

      const value = control.value.toString().trim();

      // Verifica che sia esattamente 10 caratteri alfanumerici
      const cigPattern = /^[A-Za-z0-9]{10}$/;

      if (!cigPattern.test(value)) {
        return {
          cigInvalid: {
            message: 'Il CIG deve essere composto da esattamente 10 caratteri alfanumerici'
          }
        };
      }

      return null;
    };
  }

  // Validatore per il campo CUP (15 caratteri alfanumerici)
  cupValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Se il campo è vuoto, non è obbligatorio per default
      }

      const value = control.value.toString().trim();

      // Verifica che sia esattamente 15 caratteri alfanumerici
      const cupPattern = /^[A-Za-z0-9]{15}$/;

      if (!cupPattern.test(value)) {
        return {
          cupInvalid: {
            message: 'Il CUP deve essere composto da esattamente 15 caratteri alfanumerici'
          }
        };
      }

      return null;
    };
  }

  // Validatore per assicurare che tutti i documenti abbiano la stessa linea di finanziamento
  lineaFinanziamentoUnicaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Se il campo è vuoto, lascia che required si occupi della validazione
      }

      // 1. Ottieni la linea di finanziamento dai beneficiari esistenti
      const lineaFinanziamentoEsistente = this.getLineaFinanziamentoBeneficiariEsistenti();

      if (lineaFinanziamentoEsistente) {
        // Controlla se la linea selezionata corrisponde a quella esistente
        if (control.value !== lineaFinanziamentoEsistente) {
          const lineaLabel = LineeFinanziamentoLabelMapping[lineaFinanziamentoEsistente] || lineaFinanziamentoEsistente;
          return {
            lineaFinanziamentoDiversa: {
              message: `Tutti i documenti di questa nota devono avere la stessa linea di finanziamento. Linea richiesta: ${lineaLabel}`
            }
          };
        }
      }

      // 2. Controlla che tutti i documenti dello stesso beneficiario abbiano la stessa linea
      if (this.formBeneficiario && this.documentiSpesaArray) {
        const lineaFinanziamentoBeneficiario = this.getLineaFinanziamentoDocumentiBeneficiario();

        if (lineaFinanziamentoBeneficiario && control.value !== lineaFinanziamentoBeneficiario) {
          const lineaLabel = LineeFinanziamentoLabelMapping[lineaFinanziamentoBeneficiario] || lineaFinanziamentoBeneficiario;
          return {
            lineaFinanziamentoDiversa: {
              message: `Tutti i documenti dello stesso beneficiario devono avere la stessa linea di finanziamento. Linea richiesta: ${lineaLabel}`
            }
          };
        }
      }

      return null;
    };
  }

  // Metodo helper per ottenere la linea di finanziamento dai documenti dello stesso beneficiario
  private getLineaFinanziamentoDocumentiBeneficiario(): LineeFinanziamentoEnum | null {
    if (!this.documentiSpesaArray || this.documentiSpesaArray.length === 0) {
      return null;
    }

    // Trova il primo documento che ha una linea di finanziamento selezionata
    for (let i = 0; i < this.documentiSpesaArray.length; i++) {
      const documento = this.documentiSpesaArray.at(i);
      const lineaFinanziamento = documento.get('lineaFinanziamento').value;
      if (lineaFinanziamento) {
        return lineaFinanziamento;
      }
    }

    return null;
  }

  // Metodo helper per ottenere la linea di finanziamento dai beneficiari esistenti
  private getLineaFinanziamentoBeneficiariEsistenti(): LineeFinanziamentoEnum | null {
    if (!this.beneficiariEsistenti || this.beneficiariEsistenti.length === 0) {
      return null;
    }

    // Ottieni la linea di finanziamento dal primo documento del primo beneficiario
    for (const beneficiario of this.beneficiariEsistenti) {
      const documentiSpesa = beneficiario.get('documentiSpesa');
      if (documentiSpesa && documentiSpesa.value && documentiSpesa.value.length > 0) {
        const primoDocumento = documentiSpesa.value[0];
        if (primoDocumento && primoDocumento.lineaFinanziamento) {
          return primoDocumento.lineaFinanziamento;
        }
      }
    }

    return null;
  }

  // Validatore per la scadenza DURC
  scadenzaDurcValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Se il campo è vuoto, lascia che required si occupi della validazione
      }

      const dataScadenza = new Date(control.value);
      const oggi = new Date();
      oggi.setHours(0, 0, 0, 0); // Resetta ore per confronto solo sulla data

      // Per ALTRO, deve essere successiva a oggi
      if (this.tipoNota === TipoNotaLiquidazioneEnum.ALTRO) {
        if (dataScadenza <= oggi) {
          return {
            dataInvalida: {
              message: 'La scadenza DURC deve essere successiva alla data odierna'
            }
          };
        }
      }

      // Per LIQUIDAZIONE_FATTURA_COMMERCIALE nessuna soglia dei 10 giorni,
      // ma una data già scaduta non è comunque accettabile (stessa regola
      // applicata in clonazione da isScadenzaDurcEreditataValida)
      if (this.tipoNota === TipoNotaLiquidazioneEnum.LIQUIDAZIONE_FATTURA_COMMERCIALE) {
        if (dataScadenza < oggi) {
          return {
            dataInvalida: {
              message: 'La scadenza DURC non può essere una data già passata'
            }
          };
        }
      }

      // Per LIQUIDAZIONE_CONTRIBUTI e PAGAMENTO_IMPOSTE, deve essere successiva di almeno 10 giorni a oggi
      if (this.tipoNota === TipoNotaLiquidazioneEnum.LIQUIDAZIONE_CONTRIBUTI ||
        this.tipoNota === TipoNotaLiquidazioneEnum.PAGAMENTO_IMPOSTE) {
        const dataMinima = new Date();
        dataMinima.setDate(dataMinima.getDate() + 10);
        dataMinima.setHours(0, 0, 0, 0);

        if (dataScadenza < dataMinima) {
          return {
            dataInvalida: {
              message: 'La scadenza DURC deve essere successiva di almeno 10 giorni alla data odierna'
            }
          };
        }
      }

      return null;
    };
  }

  // Metodo helper per determinare i validatori dell'IBAN
  private getIbanValidators(): ValidatorFn[] {
    // Se è pagamento imposte e non è persona fisica, IBAN non è obbligatorio
    if (this.tipoNota === TipoNotaLiquidazioneEnum.PAGAMENTO_IMPOSTE) {
      const tipoBeneficiarioControl = this.formBeneficiario && this.formBeneficiario.get('tipoBeneficiario');
      if (tipoBeneficiarioControl && tipoBeneficiarioControl.value !== TipoBeneficiarioEnum.PERSONA_FISICA) {
        return [this.ibanValidator()]; // Solo validatore formato, non obbligatorio
      }
    }
    return [this.ibanValidator(), Validators.required]; // Validatore formato + obbligatorio
  }

  // Metodo helper per determinare i validatori della ritenuta
  private getRitenutaValidators(): ValidatorFn[] {
    // Se è pagamento imposte e non è persona fisica, ritenuta non ha validatori specifici
    if (this.tipoNota === TipoNotaLiquidazioneEnum.PAGAMENTO_IMPOSTE) {
      const tipoBeneficiarioControl = this.formBeneficiario && this.formBeneficiario.get('tipoBeneficiario');
      if (tipoBeneficiarioControl && tipoBeneficiarioControl.value !== TipoBeneficiarioEnum.PERSONA_FISICA) {
        return []; // Nessun validatore
      }
    }
    return []; // Di default la ritenuta non ha validatori obbligatori
  }

  // Metodo per determinare se l'IBAN è obbligatorio
  isIbanRequired(): boolean {
    if (this.tipoNota === TipoNotaLiquidazioneEnum.PAGAMENTO_IMPOSTE) {
      const tipoBeneficiarioControl = this.formBeneficiario && this.formBeneficiario.get('tipoBeneficiario');
      if (tipoBeneficiarioControl && tipoBeneficiarioControl.value !== TipoBeneficiarioEnum.PERSONA_FISICA) {
        return false; // Non obbligatorio per pagamenti imposte se non persona fisica
      }
    }
    return true; // Obbligatorio negli altri casi
  }

  // Metodo per determinare se l'IBAN è visibile
  isIbanVisible(): boolean {
    if (this.tipoNota === TipoNotaLiquidazioneEnum.PAGAMENTO_IMPOSTE) {
      const tipoBeneficiarioControl = this.formBeneficiario && this.formBeneficiario.get('tipoBeneficiario');
      if (tipoBeneficiarioControl && tipoBeneficiarioControl.value !== TipoBeneficiarioEnum.PERSONA_FISICA) {
        return false; // Non visibile per pagamenti imposte se non persona fisica
      }
    }
    return true; // Visibile negli altri casi
  }

  // Metodo per determinare se la ritenuta è visibile
  isRitenutaVisible(): boolean {
    if (this.tipoNota === TipoNotaLiquidazioneEnum.PAGAMENTO_IMPOSTE) {
      const tipoBeneficiarioControl = this.formBeneficiario && this.formBeneficiario.get('tipoBeneficiario');
      if (tipoBeneficiarioControl && tipoBeneficiarioControl.value !== TipoBeneficiarioEnum.PERSONA_FISICA) {
        return false; // Non visibile per pagamenti imposte se non persona fisica
      }
    }
    return true; // Visibile negli altri casi
  }

  // Metodo per determinare se il checkbox "Pagamento soggetto a IVA" è visibile
  isSoggettoIvaCheckboxVisible(): boolean {
    // Visibile solo per liquidazione fattura commerciale
    return this.tipoNota === TipoNotaLiquidazioneEnum.LIQUIDAZIONE_FATTURA_COMMERCIALE;
  }

  // Metodo per determinare se i campi IVA e importo netto sono visibili per un documento
  isIvaFieldsVisible(documentoIndex: number): boolean {
    // Per liquidazione contributi, i campi IVA non sono mai visibili
    if (this.tipoNota === TipoNotaLiquidazioneEnum.LIQUIDAZIONE_CONTRIBUTI) {
      return false;
    }

    // Per liquidazione fattura commerciale, visibili solo se soggettoIva == true
    if (this.tipoNota === TipoNotaLiquidazioneEnum.LIQUIDAZIONE_FATTURA_COMMERCIALE) {
      const documentoGroup = this.documentiSpesaArray.at(documentoIndex) as FormGroup;
      const soggettoIvaControl = documentoGroup && documentoGroup.get('soggettoIva');
      return soggettoIvaControl && soggettoIvaControl.value === true;
    }

    return false;
  }

  // Metodo per determinare se il campo importo (senza IVA) è visibile per un documento
  isImportoSenzaIvaVisible(documentoIndex: number): boolean {
    // Per liquidazione contributi, mostra sempre il campo importo
    if (this.tipoNota === TipoNotaLiquidazioneEnum.LIQUIDAZIONE_CONTRIBUTI) {
      return true;
    }

    // Per tipologia ALTRO, mostra sempre il campo importo (non ha IVA)
    if (this.tipoNota === TipoNotaLiquidazioneEnum.ALTRO) {
      return true;
    }

    // Per pagamento imposte, mostra sempre il campo importo (non ha IVA)
    if (this.tipoNota === TipoNotaLiquidazioneEnum.PAGAMENTO_IMPOSTE) {
      return true;
    }

    // Per altre tipologie, visibile solo se soggettoIva == false
    const documentoGroup = this.documentiSpesaArray.at(documentoIndex) as FormGroup;
    const soggettoIvaControl = documentoGroup && documentoGroup.get('soggettoIva');
    return soggettoIvaControl && soggettoIvaControl.value === false;
  }

  // Metodo helper per creare il contenuto CF/PI per le tabelle PDF
  // Da utilizzare al posto di item.cfPi nella generazione delle celle
  getCfPiDisplayValue(beneficiario: any): string {
    const parts: string[] = [];

    if (beneficiario.codiceFiscale) {
      parts.push(`CF: ${beneficiario.codiceFiscale}`);
    }

    if (beneficiario.partitaIva) {
      parts.push(`PI: ${beneficiario.partitaIva}`);
    }

    return parts.join(' - ');
  }

}
